import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { searchApiDebounced, cancelSearch } from "../../store/thunks/searchApiThunk";
import { setQuery, clearResults } from "../../store/slices/searchApiSlice";

const SearchDropdown = ({
  displayFields,
  onSelection,
  prefix,
  placeholder,
  initialSelection,
  initialValue,
  activeServices,
  separator = ", ", // Default to comma-space, but allow customization
}) => {
  const dispatch = useDispatch();
  const { results = [], loading, error } = useSelector((state) => state.search);
  const [searchQuery, setSearchQuery] = useState(initialValue || "");
  const [selectedItem, setSelectedItem] = useState(initialSelection || null);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    dispatch(clearResults());
    
    // Cleanup function to cancel search when component unmounts
    return () => {
      cancelSearch();
    };
  }, [dispatch]);

  useEffect(() => {
    if (initialValue) {
      setSearchQuery(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (searchQuery && searchQuery.length > 0) {
      const prefixedQuery = prefix ? `${prefix}${searchQuery}` : searchQuery;
      dispatch(setQuery(prefixedQuery));
      // Use debounced search instead of immediate search
      dispatch(searchApiDebounced(prefixedQuery));
    } else {
      dispatch(clearResults());
    }
  }, [searchQuery, prefix, dispatch]);

  useEffect(() => {
    if (initialSelection) {
      setSelectedItem(initialSelection);
      setSearchQuery(
        displayFields
          .map((field) => getFieldDisplay(initialSelection, field))
          .filter(Boolean)
          .join(separator)
      );
    }
  }, [initialSelection, displayFields, separator]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        // Only clear search query if no item is selected
        if (!selectedItem) {
          setSearchQuery("");
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef, selectedItem]);

  const handleSelection = (item) => {
    setSelectedItem(item);
    setSearchQuery(
      displayFields
        .map((field) => getFieldDisplay(item, field))
        .filter(Boolean)
        .join(separator)
    );
    setIsOpen(false);
    if (onSelection) {
      onSelection(item);
    }
  };

  const handleNewSearch = (value) => {
    setSearchQuery(value);
    setSelectedItem(null);
    if (!value) {
      dispatch(clearResults());
    }
    setIsOpen(true);
  };

  const getFieldDisplay = (item, field) => {
    if (field === "client.fullName") {
      return item.client
        ? `${item.client.firstName || ""} ${item.client.lastName || ""}`.trim()
        : "N/A";
    }

    if (field === "clinician.fullName") {
      return item.clinician
        ? `${item.clinician.firstName || ""} ${item.clinician.lastName || ""
          }`.trim()
        : "N/A";
    }

    if (field === "phoneNumbers" && Array.isArray(item.phoneNumbers)) {
      return item.phoneNumbers
        .map((phone) => `${phone.type}: ${phone.number}`)
        .join(", ");
    }

    const fieldParts = field.split(".");
    let value = item;

    for (const part of fieldParts) {
      if (!value || typeof value !== "object") {
        return "";
      }
      value = value[part];
    }

    return value || "";
  };

  const filteredResults = activeServices
    ? (Array.isArray(results) ? results.filter((service) => service.active) : [])
    : (Array.isArray(results) ? results : []);

  return (
    <div ref={wrapperRef} className="relative">
      <Listbox as="div" value={selectedItem} onChange={handleSelection}>
        {({ open }) => (
          <div className="relative">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleNewSearch(e.target.value)}
                onClick={() => setIsOpen(true)}
                className="w-full rounded-sm border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={placeholder}
              />
            </div>

            <Transition
              show={isOpen && (filteredResults.length > 0 || loading)}
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95">
              <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-sm border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                {loading && (
                  <div className="flex items-center justify-center py-2 text-sm text-gray-500">
                    <div className="h-4 w-4 animate-spin rounded-sm border-2 border-gray-300 border-t-indigo-600" />
                    <span className="ml-2">Loading...</span>
                  </div>
                )}
                {!loading && filteredResults.length === 0 && searchQuery && (
                  <div className="py-2 pl-3 pr-9 text-sm text-gray-500">
                    No results found
                  </div>
                )}
                {filteredResults.map((item) => {
                  const displayText = displayFields
                    .map((field) => getFieldDisplay(item, field))
                    .filter(Boolean)
                    .join(separator);

                  if (displayText) {
                    return (
                      <Listbox.Option
                        key={item._id}
                        value={item}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 transition-colors ${
                            active
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700"
                          }`
                        }
                        onClick={() => handleSelection(item)}>
                        <div className="flex items-center">
                          <span className="truncate font-medium">
                            {displayText}
                          </span>
                        </div>
                        {selectedItem &&
                          displayFields.every(
                            (field) =>
                              getFieldDisplay(selectedItem, field) ===
                              getFieldDisplay(item, field)
                          ) && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">
                              <CheckIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                      </Listbox.Option>
                    );
                  }
                  return null;
                })}
                {error && (
                  <div className="py-2 pl-3 pr-9 text-sm text-red-500">
                    {error}
                  </div>
                )}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  );
};

export default SearchDropdown;
